import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import { OPEKEPE_SYNC_SCOPE } from "constants/opekepe";
import { userFullName } from "utils/namingUtils";
import Modal from "components/Modals/Modal";
import OpekepeSync from "components/opekepe-sync/OpekepeSync";
import { Button, OpekepeSynced } from "components";
import { FETCH_STATE } from "constants/fetchState";
import { useToggle } from "hooks/useToggle";

const ModalStyled = styled(Modal)({
  "& .opekepe-sync-wrapper": {
    height: "max-content",
    width: "500px",
    "& .opekepe-sync-body": {
      display: "flex",
      justifyContent: "center",
      padding: "2rem",
    },
    "& .opekepe-sync-body-column": {
      width: "100%",
    },
    "& .resync-btn": {
      marginTop: "2rem",
      borderColor: "#04303B",
      color: "#04303B",
    },
  },
});

const OpekepeSyncModal = ({ isOpen, onClose, user }) => {
  const { t } = useTranslation();

  const userMapped = useMemo(() => {
    let latestApplication = null;

    if (user?.opekepeApplications?.length) {
      for (const app of user.opekepeApplications) {
        if (!latestApplication || app.year > latestApplication.year) {
          latestApplication = app;
        }
      }
    }

    return {
      id: user?.id,
      name: userFullName({ user }),
      vat: user?.taxNumber,
      isSynced: Boolean(user?.syncDate),
      opekepeApplications: user?.opekepeApplications,
      latestApplication,
    };
  }, [user]);

  const {
    isOpen: isReSyncing,
    open: startReSyncing,
    close: cancelReSyncing,
  } = useToggle();

  const [isReSynced, setIsReSynced] = useState(false);
  const handleSyncSuccess = useCallback(() => {
    setIsReSynced(true);
  }, []);

  const handleClose = useCallback(() => {
    if (isReSynced) {
      window.location.reload();
      return;
    }
    onClose?.();
  }, [isReSynced, onClose]);

  return (
    <ModalStyled
      isOpen={isOpen}
      onClose={handleClose}
      classNames={{
        wrapper: "opekepe-sync-wrapper",
        body: "opekepe-sync-body",
      }}
      title={t("KydTable.SyncWithOpekepe")}
    >
      {userMapped.isSynced && !isReSyncing ? (
        <div className="opekepe-sync-body-column">
          <OpekepeSynced
            fetchState={FETCH_STATE.SUCCESS}
            data={userMapped.latestApplication}
          />
          <Button
            corners="square"
            isFullwidth
            className="resync-btn"
            onClick={startReSyncing}
          >
            {t("OpekepeSync.ReSyncWithOpekepe")}
          </Button>
        </div>
      ) : (
        <div className="opekepe-sync-body-column">
          <OpekepeSync
            scope={OPEKEPE_SYNC_SCOPE.KYD}
            user={userMapped}
            isFullWidth
            onSyncSuccess={handleSyncSuccess}
          />
          {isReSyncing && (
            <Button
              corners="square"
              isFullwidth
              className="resync-btn"
              onClick={cancelReSyncing}
            >
              {t("OpekepeSync.CancelResyncWithOpekepe")}
            </Button>
          )}
        </div>
      )}
    </ModalStyled>
  );
};

export default OpekepeSyncModal;
